import { SurveyData, AnalyticsData, FilterOptions, Workshop, ImplementationPartner } from '../types';

// Workshop mapping
export const workshops: Workshop[] = [
  { id: "01", name: "Entrepreneurship Development Program (ESDP)" },
  { id: "02", name: "Business Development Services (BDS)" },
  { id: "03", name: "Zero Defect Zero Effect (ZED)" },
  { id: "04", name: "Intellectual Property Rights (IPR)" },
  { id: "05", name: "Reverse Buyer Seller Meet (RBSM)" },
  { id: "06", name: "Udyam Registration" },
  { id: "07", name: "Vendor Development Program (VDP)" },
  { id: "08", name: "Modern & Green Package" },
  { id: "09", name: "Trade Receivables electronic Discounting System (TReDS)" },
  { id: "10", name: "National Research Development Corporation (NRDC)" },
  { id: "11", name: "National Stock Exchange (NSE)" },
  { id: "12", name: "National Productivity Council (NPC)" }
];

// Module-level cache for partners and departments (populated from API)
let partnersCache: ImplementationPartner[] = [];
let departmentsCache: ImplementationPartner[] = [];

/**
 * Set the partners cache (called from PartnersProvider)
 */
export function setPartnersCache(partners: Array<{ id: string; name: string }>): void {
  partnersCache = partners.map(p => ({ id: p.id, name: p.name }));
}

/**
 * Set the departments cache (called from PartnersProvider)
 */
export function setDepartmentsCache(departments: Array<{ id: string; name: string }>): void {
  departmentsCache = departments.map(d => ({ id: d.id, name: d.name }));
}

// Legacy exports for backward compatibility (now use cache)
// These are getter functions that return the cached arrays
export function getImplementationPartners(): ImplementationPartner[] {
  return partnersCache;
}

export function getDepartments(): ImplementationPartner[] {
  return departmentsCache;
}

// For backward compatibility, export as getter properties
// Note: These will be empty until cache is populated from API
// Components should use getImplementationPartners() and getDepartments() instead
export const implementationPartners: ImplementationPartner[] = [];
export const departments: ImplementationPartner[] = [];

// Update the exported arrays when cache is populated (called from PartnersContext)
// This allows components that import the arrays directly to still work
export function syncExportedArrays(): void {
  (implementationPartners as any).length = 0;
  (departments as any).length = 0;
  implementationPartners.push(...partnersCache);
  departments.push(...departmentsCache);
}

// Districts list
export const districts: string[] = [
  "S.P.S.R. Nellore",
  "Nandyala", 
  "Srikakulam",
  "Visakhapatnam",
  "Guntur",
  "Kurnool",
  "Palnadu",
  "Chittoor",
  "Krishna",
  "Prakasam",
  "Anantapuram",
  "West Godavari",
  "East Godavari",
  "Eluru",
  "NTR",
  "Bapatla",
  "Tirupati",
  "Annamayya",
  "Sri Satya Sai",
  "Vizianagaram",
  "Anakapalli",
  "Kakinada",
  "Konaseema",
  "Alluri Seetharama Raju",
  "Parvathipuram Manyam",
  "YSR"
];

// Workshop targets (batches/workshops or participants)
export const WORKSHOP_TARGETS: Record<string, { target: number; type: 'workshops' | 'participants' }> = {
  '06': { target: 2600, type: 'workshops' },
  '01': { target: 2184, type: 'workshops' },
  '02': { target: 1200, type: 'participants' },
  '04': { target: 182, type: 'workshops' },
  '05': { target: 10, type: 'workshops' },
  '07': { target: 91, type: 'workshops' },
  '08': { target: 104, type: 'workshops' },
  '03': { target: 1638, type: 'workshops' },
  '09': { target: 364, type: 'workshops' },
};

// Helper functions
export const getWorkshopName = (workshopId: string): string => {
  const workshop = workshops.find(w => w.id === workshopId);
  return workshop ? workshop.name : `Workshop ${workshopId}`;
};

export const getWorkshopTarget = (workshopId: string): { target: number; type: 'workshops' | 'participants' } | null => {
  return WORKSHOP_TARGETS[workshopId] || null;
};

export const getPartnerName = (partnerId: string): string => {
  const partner = partnersCache.find(p => p.id === partnerId);
  return partner ? partner.name : partnerId;
};

export const getDepartmentName = (departmentId: string): string => {
  const department = departmentsCache.find(d => d.id === departmentId);
  return department ? department.name : departmentId;
};

// Categorize implementation partner ID
// Now uses database cache instead of pattern matching
export const categorizeImplementationPartner = (partnerId: string): 'implementation_partner' | 'department' => {
  // Check cache first
  if (departmentsCache.find(d => d.id === partnerId)) {
    return 'department';
  }
  if (partnersCache.find(p => p.id === partnerId)) {
    return 'implementation_partner';
  }
  // Fallback to pattern matching if cache not populated yet
  if (partnerId.startsWith('DIO-') || partnerId.startsWith('ANO-')) {
    return 'department';
  }
  return 'implementation_partner';
};

// Filter data by category
export const filterDataByCategory = (data: SurveyData[], category: 'implementation_partner' | 'department'): SurveyData[] => {
  return data.filter(item => {
    const itemCategory = categorizeImplementationPartner(item.implementationPartnerId);
    return itemCategory === category;
  });
};

// Filter data based on filter options
export const filterData = (data: SurveyData[], filters: FilterOptions): SurveyData[] => {
  return data.filter(item => {
    // Workshop filter
    if (filters.workshops.length > 0 && !filters.workshops.includes(item.workshopId)) {
      return false;
    }

    // District filter
    if (filters.districts.length > 0 && !filters.districts.includes(item.district)) {
      return false;
    }

    // Implementation partner filter
    if (filters.implementationPartners.length > 0 && !filters.implementationPartners.includes(item.implementationPartnerId)) {
      return false;
    }

    // Date range filter
    if (filters.dateRange.start && filters.dateRange.end) {
      const responseDate = new Date(item.responseTimestamp);
      const startDate = new Date(filters.dateRange.start);
      const endDate = new Date(filters.dateRange.end);
      
      if (responseDate < startDate || responseDate > endDate) {
        return false;
      }
    }

    return true;
  });
};

// Analytics calculation
export const calculateAnalytics = (data: SurveyData[]): AnalyticsData => {
  if (data.length === 0) {
    return {
      totalResponses: 0,
      totalBatches: 0,
      totalCandidates: 0,
      averageRating: 0,
      responseRate: 0,
      yesPercentage: {
        q1: 0,
        q2: 0,
        q3: 0,
        q4: 0,
        q5: 0
      },
      ratingDistribution: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 },
      responsesByWorkshop: {},
      districtWiseData: [],
      workshopWiseData: [],
      partnerWiseData: []
    };
  }

  const totalResponses = data.length;
  
  // Calculate average rating
  const totalRating = data.reduce((sum, item) => sum + item.response.rating, 0);
  const averageRating = totalRating / totalResponses;

  // Calculate unique batches and candidates
  const uniqueBatches = new Set(data.map(item => item.batchId));
  const uniqueCandidates = new Set(data.map(item => item.candidateId));

  // Calculate yes percentages for each question
  const yesCounts = {
    q1: data.filter(item => item.response.q1 === 'YES').length,
    q2: data.filter(item => item.response.q2 === 'YES').length,
    q3: data.filter(item => item.response.q3 === 'YES').length,
    q4: data.filter(item => item.response.q4 === 'YES').length,
    q5: data.filter(item => item.response.q5 === 'YES').length
  };

  const yesPercentage = {
    q1: Math.round((yesCounts.q1 / totalResponses) * 100 * 100) / 100,
    q2: Math.round((yesCounts.q2 / totalResponses) * 100 * 100) / 100,
    q3: Math.round((yesCounts.q3 / totalResponses) * 100 * 100) / 100,
    q4: Math.round((yesCounts.q4 / totalResponses) * 100 * 100) / 100,
    q5: Math.round((yesCounts.q5 / totalResponses) * 100 * 100) / 100
  };

  // Calculate rating distribution
  const ratingDistribution: Record<number, number> = {
    1: 0, 2: 0, 3: 0, 4: 0, 5: 0
  };
  data.forEach(item => {
    ratingDistribution[item.response.rating]++;
  });

  // Calculate responses by workshop
  const responsesByWorkshop: Record<string, number> = {};
  data.forEach(item => {
    responsesByWorkshop[item.workshopId] = (responsesByWorkshop[item.workshopId] || 0) + 1;
  });

  // Calculate district-wise data
  const districtMap = new Map<string, { totalResponses: number; totalRating: number; candidates: Set<string> }>();
  data.forEach(item => {
    const district = item.district;
    if (!districtMap.has(district)) {
      districtMap.set(district, { totalResponses: 0, totalRating: 0, candidates: new Set() });
    }
    const districtData = districtMap.get(district)!;
    districtData.totalResponses++;
    districtData.totalRating += item.response.rating;
    districtData.candidates.add(item.candidateId);
  });

  const districtWiseData = Array.from(districtMap.entries()).map(([district, data]) => ({
    district,
    totalResponses: data.totalResponses,
    averageRating: Math.round((data.totalRating / data.totalResponses) * 100) / 100,
    responseRate: 0, // This would need total candidates per district
  }));

  // Calculate workshop-wise data
  const workshopMap = new Map<string, { totalResponses: number; totalRating: number; candidates: Set<string> }>();
  data.forEach(item => {
    const workshopId = item.workshopId;
    if (!workshopMap.has(workshopId)) {
      workshopMap.set(workshopId, { totalResponses: 0, totalRating: 0, candidates: new Set() });
    }
    const workshopData = workshopMap.get(workshopId)!;
    workshopData.totalResponses++;
    workshopData.totalRating += item.response.rating;
    workshopData.candidates.add(item.candidateId);
  });

  const workshopWiseData = Array.from(workshopMap.entries()).map(([workshopId, data]) => ({
    workshopId,
    workshopName: getWorkshopName(workshopId),
    totalResponses: data.totalResponses,
    averageRating: Math.round((data.totalRating / data.totalResponses) * 100) / 100,
    responseRate: 0, // This would need total candidates per workshop
  }));

  // Calculate partner-wise data
  const partnerMap = new Map<string, { totalResponses: number; totalRating: number; candidates: Set<string> }>();
  data.forEach(item => {
    const partnerId = item.implementationPartnerId;
    if (!partnerMap.has(partnerId)) {
      partnerMap.set(partnerId, { totalResponses: 0, totalRating: 0, candidates: new Set() });
    }
    const partnerData = partnerMap.get(partnerId)!;
    partnerData.totalResponses++;
    partnerData.totalRating += item.response.rating;
    partnerData.candidates.add(item.candidateId);
  });

  const partnerWiseData = Array.from(partnerMap.entries()).map(([partnerId, data]) => ({
    partnerId,
    partnerName: getPartnerName(partnerId),
    totalResponses: data.totalResponses,
    averageRating: Math.round((data.totalRating / data.totalResponses) * 100) / 100,
    responseRate: 0, // This would need total candidates per partner
  }));

  return {
    totalResponses,
    totalBatches: uniqueBatches.size,
    totalCandidates: uniqueCandidates.size,
    averageRating: Math.round(averageRating * 100) / 100,
    responseRate: 0, // This would need total candidates count
    yesPercentage,
    ratingDistribution,
    responsesByWorkshop,
    districtWiseData,
    workshopWiseData,
    partnerWiseData,
  };
};

// Helper function to format numbers
export const formatNumber = (num: number): string => {
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1) + 'M';
  } else if (num >= 1000) {
    return (num / 1000).toFixed(1) + 'K';
  }
  return num.toString();
};

// Helper function to format percentage
export const formatPercentage = (num: number): string => {
  return `${num.toFixed(1)}%`;
};

// Helper function to get color for rating
export const getRatingColor = (rating: number): string => {
  if (rating >= 4) return '#10B981'; // Green
  if (rating >= 3) return '#F59E0B'; // Yellow
  return '#EF4444'; // Red
};

// Helper function to get color for percentage
export const getPercentageColor = (percentage: number): string => {
  if (percentage >= 80) return '#10B981'; // Green
  if (percentage >= 60) return '#F59E0B'; // Yellow
  return '#EF4444'; // Red
};
